// src/components/Plans.js
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/* ===================== DATA ===================== */

const MAIN_TABS = [
  'Internet Inalámbrico Fijo',
  'Internet 5G Ilimitado',
  'Fibra Óptica',
];

const SUB_TABS = ['Residencial', 'Comercial'];

const PLANS = {
  'Internet Inalámbrico Fijo': {
    Residencial: [
      {
        nameKey: 'plan_basic',
        speed: '25/5 Mbps',
        price: '$44.95',
        popular: false,
        features: [
          'feature_fixed_wireless',
          'feature_unlimited',
          'feature_support_24_7',
          'feature_wifi_free',
        ],
      },
      {
        nameKey: 'plan_standard',
        speed: '50/10 Mbps',
        price: '$54.95',
        popular: true,
        features: [
          'feature_fixed_wireless',
          'feature_unlimited',
          'feature_support_24_7',
          'feature_wifi_free',
        ],
      },
      {
        nameKey: 'plan_premium',
        speed: '100/25 Mbps',
        price: '$79.95',
        popular: false,
        features: [
          'feature_fixed_wireless',
          'feature_unlimited',
          'feature_support_24_7',
          'feature_wifi_free',
        ],
      },
    ],
    Comercial: [
      {
        nameKey: 'plan_basic',
        speed: '50/10 Mbps',
        price: '$89.95',
        popular: false,
        features: [
          'feature_fixed_wireless',
          'feature_static_ip',
          'feature_priority_support',
        ],
      },
      {
        nameKey: 'plan_standard',
        speed: '75/15 Mbps',
        price: '$109.95',
        popular: true,
        features: [
          'feature_fixed_wireless',
          'feature_static_ip',
          'feature_redundancy',
        ],
      },
      {
        nameKey: 'plan_premium',
        speed: '100/15 Mbps',
        price: '$149.95',
        popular: false,
        features: [
          'feature_fixed_wireless',
          'feature_static_ip',
          'feature_sla',
        ],
      },
    ],
  },

  'Internet 5G Ilimitado': [
    {
      nameKey: 'plan_5g_unlimited',
      speed: 'Hasta 100 Mbps',
      price: '$74.95',
      popular: true,
      features: [
        'feature_unlimited',
        'feature_5g_network',
        'feature_download_100',
        'feature_upload_25',
        'feature_support_24_7',
      ],
    },
  ],

  'Fibra Óptica': {
    Residencial: [
      {
        nameKey: 'plan_fiber_300',
        speed: '300 Mbps',
        price: '$69.95',
        popular: false,
        features: [
          'feature_fiber_dedicated',
          'feature_low_latency',
          'feature_router_included',
        ],
      },
      {
        nameKey: 'plan_fiber_600',
        speed: '600 Mbps',
        price: '$89.95',
        popular: true,
        features: [
          'feature_fiber_dedicated',
          'feature_low_latency',
          'feature_router_premium',
        ],
      },
      {
        nameKey: 'plan_fiber_1g',
        speed: '1 Gbps',
        price: '$119.95',
        popular: false,
        features: [
          'feature_fiber_dedicated',
          'feature_max_speed',
          'feature_priority_support',
        ],
      },
    ],
    Comercial: [
      {
        nameKey: 'plan_fiber_business_300',
        speed: '300 Mbps',
        price: '$149.95',
        popular: false,
        features: [
          'feature_business_fiber',
          'feature_static_ip',
          'feature_priority_support',
        ],
      },
      {
        nameKey: 'plan_fiber_business_600',
        speed: '600 Mbps',
        price: '$199.95',
        popular: true,
        features: [
          'feature_business_fiber',
          'feature_static_ip',
          'feature_sla_guaranteed',
        ],
      },
      {
        nameKey: 'plan_fiber_business_1g',
        speed: '1 Gbps',
        price: '$299.95',
        popular: false,
        features: [
          'feature_business_fiber',
          'feature_static_ip',
          'feature_sla_premium',
        ],
      },
    ],
  },
};

/* ===================== COMPONENT ===================== */

const Plans = () => {
  const { t } = useTranslation();

  const [mainTab, setMainTab] = useState('Internet Inalámbrico Fijo');
  const [subTab, setSubTab] = useState('Residencial');

  const requiresSubTab =
    typeof PLANS[mainTab] === 'object' &&
    !Array.isArray(PLANS[mainTab]);

  const plansToShow = requiresSubTab
    ? PLANS[mainTab][subTab]
    : PLANS[mainTab];

  const mainTabLabels = {
    'Internet Inalámbrico Fijo': t('plans_tab_wireless'),
    'Internet 5G Ilimitado': t('plans_tab_5g'),
    'Fibra Óptica': t('plans_tab_fiber'),
  };

  const subTabLabels = {
    Residencial: t('plans_sub_residential'),
    Comercial: t('plans_sub_commercial'),
  };

  return (
    <section id="planes" className="relative z-10 pt-24 pb-32 px-6 text-white">
      <div className="container mx-auto max-w-7xl">
        {/* Title */}
        <div className="text-center mb-14">
          <h2 className="text-5xl md:text-6xl font-light mb-4">
            {t('plans_title')}
          </h2>
          <p className="text-xl text-slate-400">
            {t('plans_subtitle')}
          </p>
        </div>

        {/* Main Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-10">
          {MAIN_TABS.map(tab => {
            const tabRequiresSub =
              typeof PLANS[tab] === 'object' &&
              !Array.isArray(PLANS[tab]);

            return (
              <button
                key={tab}
                onClick={() => {
                  setMainTab(tab);
                  setSubTab(tabRequiresSub ? 'Residencial' : null);
                }}
                className={`px-8 py-4 rounded-xl font-medium text-lg transition-all ${
                  mainTab === tab
                    ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50'
                    : 'text-slate-400 border border-slate-800 hover:text-white'
                }`}
              >
                {mainTabLabels[tab]}
              </button>
            );
          })}
        </div>

        {/* Sub Tabs */}
        {requiresSubTab && (
          <div className="flex justify-center gap-4 mb-16">
            {SUB_TABS.map(tab => (
              <button
                key={tab}
                onClick={() => setSubTab(tab)}
                className={`px-8 py-4 rounded-xl font-medium text-lg transition-all ${
                  subTab === tab
                    ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50'
                    : 'text-slate-400 border border-slate-800 hover:text-white'
                }`}
              >
                {subTabLabels[tab]}
              </button>
            ))}
          </div>
        )}

        {/* Cards */}
        <div className="flex flex-wrap gap-10 justify-center">
          {plansToShow.map((plan, i) => (
            <motion.div
              key={plan.nameKey}
              layout
              transition={{ duration: 0.35, ease: 'easeOut' }}
              className={`
                group w-full md:w-[340px]
                bg-slate-900/60 backdrop-blur-xl
                p-10 rounded-3xl border
                transition-all duration-500
                ${
                  plan.popular
                    ? 'border-orange-500/70 shadow-[0_0_45px_rgba(249,115,22,0.35)]'
                    : 'border-orange-500/30'
                }
                hover:border-orange-500
                hover:scale-[1.04]
                hover:shadow-[0_0_60px_rgba(249,115,22,0.55)]
              `}
            >
              <h3 className="text-3xl text-center mb-4">
                {t(plan.nameKey)}
              </h3>

              <p className="text-4xl text-orange-400 text-center mb-4">
                {plan.speed}
              </p>

              <p className="text-3xl font-bold text-center mb-8">
                {plan.price}
                <span className="text-lg text-slate-400 font-light">
                  /{t('plans_price_suffix')}
                </span>
              </p>

              <ul className="space-y-3 mb-10">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex gap-3 text-slate-300">
                    <Check className="text-orange-400" />
                    {t(f)}
                  </li>
                ))}
              </ul>

              <a
                href="#contacto"
                className="block text-center py-4 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 font-semibold hover:scale-105 transition"
              >
                {t('plans_cta')}
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Plans;
